import { useState, useCallback } from 'react';
import axios from 'axios';

const normalizeSubscriptionStatus = (customerDetails) => {
  const subscription = customerDetails?.subscription;
  if (!subscription) {
    return {
      hasSubscription: false,
    };
  }

  const status = subscription.subscriptionStatus || subscription.status || null;
  const hasSubscription = status === 'active' || status === 'trialing' || status === 'past_due';

  return {
    hasSubscription,
    status,
    plan: subscription.subscriptionPlan || subscription.plan || null,
    trialEnd: subscription.trialEnd || subscription.trialEndsAt || null,
    subscriptionId: subscription.subscriptionId || null,
  };
};

const useSubscriptionData = ({ apiBackend, user, getAuthHeaders, setError }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(null);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);

  const createCheckoutSession = useCallback(async (priceId) => {
    try {
      setIsCreatingCheckout(true);
      const headers = await getAuthHeaders();

      const origin = window.location.origin;
      const requestData = {
        priceId: priceId || null,
        successUrl: `${origin}/subscription-success`,
        cancelUrl: `${origin}/subscription-cancel`,
      };

      const response = await axios.post(
        `${apiBackend}/api/checkout/create-session`,
        requestData,
        headers
      );

      const checkoutUrl =
        response.data?.data?.checkoutUrl ||
        response.data?.checkoutUrl ||
        response.data?.url;
      if (checkoutUrl) {
        try {
          sessionStorage.setItem('checkout_in_progress', 'true');
        } catch (e) {
          // ignore storage errors
        }
        window.location.href = checkoutUrl;
      } else {
        setIsCreatingCheckout(false);
        const errorMsg =
          response.data?.message ||
          response.data?.error ||
          response.data?.data?.message ||
          'Failed to get checkout URL from response';
        console.error('Failed response:', response.data);
        setError(`Checkout Error: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Error creating checkout session:');
      console.error('  Status:', error.response?.status);
      console.error('  Data:', error.response?.data);
      console.error('  Message:', error.message);

      const errorMessage = error.response?.data?.message || error.message || 'Failed to start trial. Please try again or contact support.';
      setError(`Stripe checkout session failed: ${errorMessage}`);
      setIsCreatingCheckout(false);
    }
  }, [apiBackend, getAuthHeaders, setError]);

  const checkSubscriptionActive = useCallback(async () => {
    if (!user) return false;
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${apiBackend}/api/account/subscription-active`,
        headers
      );
      return response.data?.data?.active === true;
    } catch (error) {
      console.error('Error checking subscription active:', error.response?.status, error.response?.data || error.message);
      return false;
    }
  }, [user, apiBackend, getAuthHeaders]);

  const fetchCustomerDetails = useCallback(async () => {
    if (!user) {
      return null;
    }

    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${apiBackend}/api/account/me`,
        headers
      );
      const data = response.data?.data || response.data;
      const normalizedSubscription = normalizeSubscriptionStatus(data);

      setCustomerDetails(data);
      setSubscriptionStatus(normalizedSubscription);
      setHasActiveSubscription(normalizedSubscription.hasSubscription);

      return normalizedSubscription;
    } catch (error) {
      console.error('Error fetching customer details:', error);
      setHasActiveSubscription(false);
      return null;
    }
  }, [user, apiBackend, getAuthHeaders]);

  return {
    subscriptionStatus,
    customerDetails,
    hasActiveSubscription,
    isCreatingCheckout,
    createCheckoutSession,
    checkSubscriptionActive,
    fetchCustomerDetails,
  };
};

export default useSubscriptionData;
